import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, TextField, Button, Divider, Grid, Breadcrumbs, Link, Tooltip, IconButton, CircularProgress,
} from '@mui/material';
import axiosInstance from '../../../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import SaveIcon from '@mui/icons-material/Save';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NumbersIcon from '@mui/icons-material/Numbers';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import BackupIcon from '@mui/icons-material/Backup';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useNavigate } from 'react-router-dom';

const SystemParameterForm = () => {
    const [params, setParams] = useState([]);
    const [formValues, setFormValues] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const UserId = sessionStorage.getItem('UserId');

    useEffect(() => {
        fetchAllParameters();
    }, []);

    const fetchAllParameters = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('UserParam/GetUserParamDashBoard', {
                start: 1,
                PageSize: 1,
                SearchText: '',
            });

            if (response.data.Status === 0) {
                const data = response.data.Data || [];
                setParams(data);

                const initialValues = {};
                data.forEach((p) => {
                    initialValues[p.UserPmId] = p.Remark || '';
                });
                setFormValues(initialValues);
            } else {
                toast.warn('No parameters returned');
            }
        } catch (err) {
            toast.error('Failed to load system parameters');
        } finally {
            setLoading(false);
        }
    };

    const handleValueChange = (id, value) => {
        setFormValues((prev) => ({ ...prev, [id]: value }));
    };

    const handleSaveAll = async () => {
        setSaving(true);
        try {
            const updates = params.map((param) => ({
                UserPmId: param.UserPmId,
                Remark: formValues[param.UserPmId] || '',
                UpdatedBy: UserId,
            }));

            const payload = {
                userParamEntities: updates,
            };

            const response = await axiosInstance.post('UserParam/UpdateUserParam', payload);

            if (response.data?.Status === 0) {
                toast.success(response.data.Message || 'Parameters updated successfully');
                fetchAllParameters();
            } else {
                toast.warn(response.data?.Message || 'Update failed');
            }
        } catch (err) {
            toast.error('Error saving changes');
        } finally {
            setSaving(false);
        }
    };

    const groupedParams = params.reduce((acc, param) => {
        const key = param.PropName || 'Others';
        if (!acc[key]) acc[key] = [];
        acc[key].push(param);
        return acc;
    }, {});

    return (
        <>
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "92%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/masters/control-security')}
                    >
                        Administrator
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        System Parameter
                    </Typography>
                </Breadcrumbs>

                <Tooltip title='Previous Page' arrow>
                    <Box sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        justifyContent: "flex-start"
                    }}
                    >
                        <IconButton
                            onClick={() => navigate('/masters/control-security')}
                            sx={{
                                backgroundColor: '#635bff',
                                borderRadius: '50%',
                                width: 35,
                                height: 35,
                                '&:hover': {
                                    backgroundColor: '#635bff',
                                },
                                boxShadow: 3,
                            }}
                        >
                            <ArrowBackIcon sx={{ color: 'white' }} />
                        </IconButton>
                    </Box>
                </Tooltip>
            </Box>

            <ToastContainer />

            <Paper
                elevation={1}
                sx={{
                    maxWidth: '90%',
                    mx: 'auto',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Box
                    sx={{
                        px: { xs: 2.5, sm: 3 },
                        py: 1,
                        bgcolor: '#635bff',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
                        <AssignmentIcon sx={{ fontSize: 22 }} />
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                                System Parameters |
                                <span style={{ fontWeight: 'normal' }}> Configure property wise parameter settings </span>
                            </Typography>
                            {/* <Typography variant="caption" sx={{ opacity: 0.88 }}>
                                Configure global application settings
                            </Typography> */}
                        </Box>
                    </Box>

                    <Button size='small' variant="contained" color="error" onClick={() => navigate('/backup')} startIcon={<BackupIcon />}>
                        Backup
                    </Button>
                </Box>

                <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
                    {loading ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : params.length === 0 ? (
                        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                            No parameters available
                        </Typography>
                    ) : (
                        <>
                            {/* <Grid
                                container
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    fontWeight: 600,
                                    color: 'black',
                                    fontSize: '0.82rem',
                                    px: 1,
                                    py: 0.8,
                                    borderBottom: '2px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Grid item xs={2}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                        <FingerprintIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                        <Typography>ID</Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={5}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                        <AssignmentIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                        <Typography>Parameter Name</Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={5}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                        <NumbersIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                        <Typography>Parameter Value</Typography>
                                    </Box>
                                </Grid>
                            </Grid> */}

                            {Object.keys(groupedParams).map((propName) => (
                                <Box key={propName} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                                    <Box sx={{ px: 2, py: 1, bgcolor: 'grey.100', fontWeight: 600 }}>
                                        {propName}
                                    </Box>

                                    <Grid container sx={{
                                        display: { xs: 'none', md: 'flex' },
                                        fontWeight: 600,
                                        color: 'black',
                                        fontSize: '0.82rem',
                                        px: 1,
                                        py: 0.8,
                                        borderBottom: '2px solid',
                                        borderColor: 'divider',
                                    }}>
                                        <Grid item xs={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                <FingerprintIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                                <Typography>ID</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                <AssignmentIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                                <Typography>Parameter Name</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                <NumbersIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                                <Typography>Parameter Value</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Section Rows */}
                                    {groupedParams[propName].map((param) => (
                                        <Grid container spacing={1} alignItems="center" key={param.UserPmId} sx={{
                                            px: 1,
                                            py: 0.8,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                        }}>
                                            <Grid item xs={12} md={2}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: { md: 'none' }, mb: 0.2 }}>
                                                    ID
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {param.UserPmId}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={5}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: { md: 'none' }, mb: 0.2 }}>
                                                    Parameter
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {param.UserPmName || '—'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={5}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: { md: 'none' }, mb: 0.2 }}>
                                                    Value
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    value={formValues[param.UserPmId] ?? ''}
                                                    onChange={(e) => handleValueChange(param.UserPmId, e.target.value)}
                                                    placeholder="Enter value"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 6,
                                                            bgcolor: 'background.paper',
                                                            height: 30,
                                                            '& fieldset': {
                                                                borderColor: 'grey.400',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'primary.main',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: 'primary.main',
                                                                borderWidth: 1.5,
                                                                boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.12)',
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            padding: '6px 20px',
                                                            fontSize: '0.9rem',
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Box>
                            ))}
                        </>
                    )}

                    <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={
                                saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
                            }
                            onClick={handleSaveAll}
                            disabled={saving || loading || params.length === 0}
                        >
                            {saving ? 'Saving...' : 'Update'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<ExitToAppIcon />}
                            onClick={() => navigate('/masters/control-security')}
                        >
                            Exit
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </>
    );
};

export default SystemParameterForm;